import React, { useState, useEffect } from 'react';
import { Container, Grid, Typography, Box, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress } from '@mui/material';
import PlanCard from '../components/PlanCard';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const PlansPage = () => {
  const [plans, setPlans] = useState([]);
  const [error, setError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await api.get('/subscriptions/plans');
        setPlans(response.data);
      } catch (err) {
        setError('Erro ao carregar os planos. Tente novamente mais tarde.');
      }
    };

    fetchPlans();
  }, []);

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setShowConfirmDialog(true);
  };

  const handleConfirmSubscription = async () => {
    if (!selectedPlan) return;

    setLoading(true);
    try {
      const response = await api.post('/subscriptions/subscribe', {
        planId: selectedPlan._id
      });

      if (response.data.init_point) {
        window.location.href = response.data.init_point;
      } else {
        // Se for plano gratuito
        setShowConfirmDialog(false);
        navigate('/provider/dashboard');
      }
    } catch (err) {
      setError('Erro ao processar a assinatura. Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        Escolha seu Plano
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 4 }}>
        <Alert severity="info">
          Promoção especial! O plano mensal está gratuito por 120 dias. Aproveite!
        </Alert>
      </Box>

      <Grid container justifyContent="center" spacing={3}>
        {plans.map((plan) => (
          <Grid item key={plan._id}>
            <PlanCard
              plan={plan}
              onSelect={handlePlanSelect}
              disabled={!plan.isActive}
              isPromotional={plan.isPromotional}
              selected={selectedPlan?._id === plan._id}
            />
          </Grid>
        ))}
      </Grid>

      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)}>
        <DialogTitle>
          Confirmar Assinatura
        </DialogTitle>
        <DialogContent>
          {selectedPlan && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Plano: {selectedPlan.name}
              </Typography>
              <Typography variant="body1" gutterBottom>
                {selectedPlan.isPromotional && selectedPlan.name === 'Mensal' ? (
                  'Você está prestes a ativar o plano mensal gratuitamente por 120 dias.'
                ) : (
                  `Valor: ${new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL'
                  }).format(selectedPlan.price)}`
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Duração: {selectedPlan.duration} dias
              </Typography>
              {selectedPlan.isPromotional && selectedPlan.name === 'Mensal' && (
                <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                  Após o período promocional, o plano será renovado automaticamente pelo valor normal.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmSubscription}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : selectedPlan?.isPromotional && selectedPlan?.name === 'Mensal' ? (
              'Ativar Plano Grátis'
            ) : (
              'Confirmar Assinatura'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PlansPage; 